import * as angular from 'angular';
import { inRange } from 'lodash';
import { BehaviorSubject, combineLatest } from 'rxjs';
import { filter } from 'rxjs/operators';

import { QueryOptions } from '@mode/shared/data-access-algolia';
import { BugsnagHelpers } from '~/lib/bugsnag-helpers';
import { Connection } from '~/modules/org-settings/lib/connection';
import { TableListData } from '@mode/shared/ui-angularjs';
import { TableListSchema } from '@mode/shared/ui-angularjs';
import { TableListValueType } from '@mode/shared/ui-angularjs';
import { SearchClientService } from '@mode/algolia/domain';

type DataSourceMultiQueryOptions = QueryOptions<'dataSource'> & { rawResults?: true };

const DEFAULT_PAGE = 0;
const DEFAULT_RESULT_PAGE_SIZE = 19;

export class ConnectionsBrowserController {
  private orgToken: string;
  private connections$: BehaviorSubject<TableListData<Connection>[]> = new BehaviorSubject([]);
  private connectionLinks$: BehaviorSubject<string[]> = new BehaviorSubject([]);
  private connectionsCurrPage$: BehaviorSubject<number> = new BehaviorSubject(null);
  private connectionsPageCount$: BehaviorSubject<number> = new BehaviorSubject(0);
  private connectionsHitsCount$: BehaviorSubject<number> = new BehaviorSubject(0);
  private query$: BehaviorSubject<string> = new BehaviorSubject(null);
  private loading = true;
  private pageNum$: BehaviorSubject<number> = new BehaviorSubject(DEFAULT_PAGE);
  private tableListSchema: TableListSchema = {
    fields: [
      {
        name: 'name',
        title: 'Display Name',
        type: TableListValueType.Html,
        tooltip: (cell) => cell.name,
      },
      {
        name: 'datasource_type',
        title: 'Datasource',
        type: TableListValueType.Html,
        tooltip: (cell) => cell.datasource_type,
      },
      {
        name: 'connection_type',
        title: 'Connection Type',
        type: TableListValueType.Text,
      },
      {
        name: 'created_at',
        title: 'Created',
        type: TableListValueType.Text,
      },
    ],
  };

  static $inject = ['SearchClientService'];

  constructor(private _searchClientService: SearchClientService) {
    const queries$ = this.query$.pipe(filter((query) => query != null));

    combineLatest(queries$, this.pageNum$).subscribe(([query, pageNum]: [string, number]) => {
      this.queryConnections(query, pageNum);
    });
  }

  $onInit() {
    this.updateQuery();
  }

  queryConnections(query = '', page = DEFAULT_PAGE) {
    const opts: DataSourceMultiQueryOptions = {
      page,
      query: query || '',
      indexes: ['dataSource'],
      rawResults: true,
      perPage: DEFAULT_RESULT_PAGE_SIZE,
      filters: {
        dataSource: `organization_${this.orgToken}`,
      },
    };
    const queryPromise = this._searchClientService.query(opts);

    return queryPromise
      .then((response) => this.handleDataSourceSearchResponse(response))
      .catch((err) => BugsnagHelpers.notifySafely(err));
  }

  handleDataSourceSearchResponse(response): void {
    const userHits = (response) => response.results[0].hits;
    const currPage = (response) => response.results[0].page;
    const pageCount = (response) => response.results[0].nbPages;
    const hitsCount = (response) => response.results[0].nbHits;
    const connectionsList = userHits(response)
      .map((hits) => this._searchClientService.createConnectionFromDatasourceHit(hits))
      .map((connection) => ({ values: connection }));
    const connectionsListLinks = userHits(response).map((dataSource) => dataSource.webPath);

    this.loading = false;
    this.connections$.next(connectionsList);
    this.connectionLinks$.next(connectionsListLinks);
    this.connectionsCurrPage$.next(currPage(response));
    this.connectionsPageCount$.next(pageCount(response));
    this.connectionsHitsCount$.next(hitsCount(response));
  }

  handleQueryUpdate($query: string): void {
    this.updateQuery($query);
  }

  handlePageSelection($page: number) {
    if (this.validPage($page, this.connectionsPageCount$.getValue())) {
      this.updatePage($page);
    }
  }

  validPage(page: number, pageRange: number): boolean {
    return inRange(page, 0, pageRange + 1);
  }

  updatePage(pageNum: number) {
    this.pageNum$.next(pageNum);
  }

  updateQuery(query = '') {
    this.resetPagination();
    this.query$.next(query);
  }

  resetPagination(): void {
    this.connectionsCurrPage$.next(0);
    this.pageNum$.next(0);
  }
}

export const ConnectionsBrowserComponent: angular.IComponentOptions = {
  controller: ConnectionsBrowserController,
  controllerAs: 'ctrl',
  bindings: {
    orgToken: '<',
  },
  template: `
    <browser>
      <browser-header>
        <capra-search-input placeholder="Search..." (text-change)="ctrl.handleQueryUpdate($event)" />
      </browser-header>

      <browser-body>
        <table-list
          ng-if="(ctrl.connections$ | async:this).length"
          schema="ctrl.tableListSchema"
          data="ctrl.connections$ | async:this"
          links="ctrl.connectionLinks$ | async:this" />

        <div class="indicator-msg" ng-if="!(ctrl.connections$ | async:this).length">
          <span ng-if="ctrl.loading">
            Loading...
          </span>
          <span ng-if="!ctrl.loading">
            No Results Found
          </span>
        </div>
      </browser-body>

      <browser-footer>
        <paginator
          curr-page="ctrl.connectionsCurrPage$ | async:this"
          page-range="ctrl.connectionsPageCount$ | async:this"
          inner-window-size="2"
          left-outer-window-size="1"
          right-outer-window-size="1"
          on-page-selection="ctrl.handlePageSelection($pageIndex)"
          num-items-page="(ctrl.connections$ | async:this).length"
          num-items-total="ctrl.connectionsHitsCount$ | async:this" />
      </browser-footer>
    </browser>
  `,
};
